// react
import { isValidElement, ReactNode } from 'react';

// types
import { ActionButton } from '../../../@types';

// mui
import {
	Button,
	Grid,
	LinearProgress,
	Paper,
	PaperProps,
	Typography,
	TypographyProps,
} from '@mui/material';

// ui
import { ChipStatus, ChipStatusProps } from '../../ui/ChipStatus/ChipStatus';
import { IconCircle, IconCircleProps } from '../../ui/IconCircle/IconCircle';
import { Loading } from '../../ui/Loading/Loading';

// theme
import { colorsSeverity } from '../../../theme';

// props
export interface CardKeyValueProps {
	actions?: ActionButton[];
	ChipStatusProps?: ChipStatusProps;
	IconCircleProps?: IconCircleProps;
	isLoading?: boolean;
	label: TypographyProps;
	labelIcon?: ReactNode;
	PaperProps?: PaperProps;
	percent?: number;
	severity?: 'default' | 'error' | 'warning' | 'info' | 'success';
	value: ReactNode | TypographyProps;
}

export const CardKeyValue = ({
	actions = [],
	ChipStatusProps,
	IconCircleProps,
	isLoading,
	label,
	labelIcon,
	PaperProps = {},
	percent,
	severity,
	value,
}: CardKeyValueProps) => {
	// vars
	const isLabelNode = isValidElement(label);
	const isValueNode = isValidElement(value);

	let { sx: sxPaper = {} } = PaperProps;

	let labelContent: ReactNode;
	let valueContent: ReactNode;

	const LabelTypographyProps: TypographyProps = {};
	const ValueTypographyProps: TypographyProps = {};

	if (severity) {
		LabelTypographyProps.color = colorsSeverity[severity].secondary;
		ValueTypographyProps.color = colorsSeverity[severity].primary;

		sxPaper = {
			...sxPaper,
			backgroundColor: colorsSeverity[severity].background,
		};
	}

	if (isLabelNode) {
		labelContent = label;
	} else {
		labelContent = (
			<Grid alignItems="center" container spacing={0.5}>
				{labelIcon && <Grid item>{labelIcon}</Grid>}
				<Grid item>
					<Typography component="div" variant="body2" {...LabelTypographyProps} {...label} />
				</Grid>
			</Grid>
		);
	}

	if (isValueNode) {
		valueContent = value;
	} else {
		valueContent = (
			<Typography
				color="brand.blue.500"
				component="div"
				fontWeight={600}
				variant="h2"
				{...ValueTypographyProps}
				{...(value as TypographyProps)}
			/>
		);
	}

	return (
		<Paper
			{...PaperProps}
			sx={(theme) => {
				return {
					padding: theme.spacing(2),
					height: '100%',
					...(sxPaper as object),
				};
			}}>
			<Grid
				alignItems="flex-end"
				container
				justifyContent="space-between"
				spacing={2}
				sx={{ height: 'calc(100% + 16px)' }}>
				<Grid item xs={typeof percent === 'number' ? 'auto' : true}>
					{isLoading ? <Loading CircularProgressProps={{ size: 30 }} /> : valueContent}
					{labelContent}
				</Grid>
				{IconCircleProps && (
					<Grid alignSelf="flex-start" item xs="auto">
						<IconCircle {...IconCircleProps} />
					</Grid>
				)}
				{ChipStatusProps && (
					<Grid alignSelf="flex-start" item xs="auto">
						<ChipStatus {...ChipStatusProps} />
					</Grid>
				)}
				{actions.length > 0 && (
					<Grid alignSelf="flex-start" item xs="auto">
						<Grid container spacing={1}>
							{actions.map((action, index) => (
								<Grid item key={index}>
									<Button {...action} />
								</Grid>
							))}
						</Grid>
					</Grid>
				)}
				{typeof percent === 'number' && !Number.isNaN(percent) && (
					<Grid item xs={true}>
						<div>
							<Typography
								color="brand.blue.700"
								component="div"
								style={{ marginLeft: `${percent}%` }}
								variant="body2">
								{percent}%
							</Typography>
							<LinearProgress value={percent} variant="determinate" />
						</div>
					</Grid>
				)}
			</Grid>
		</Paper>
	);
};
